/**
 * Pagina /superadmin/roles — Sprint 12 S12-05.
 *
 * Replica `calbucura-design-handoff/reference-pages/04-superadmin-roles.html`.
 * Lista todas las personas activas con sus roles vigentes + lote
 * actual. Cada fila tiene un boton "Editar roles" que abre el modal
 * de gestion (asignar / revocar).
 *
 * RBAC: `requireRole={['super_admin']}`. Solo el rol Admin opera roles
 * operativos (convencion #14, doctrina de roles).
 *
 * Consume:
 * - `GET /personas/` (Sprint 12 S12-05).
 * - `POST/DELETE /personas/{id}/roles[/{rol}]` (Sprint 11 v2 S11-V2-12)
 *   via el modal.
 */
import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../lib/api/client';
import { listPersonas } from '../../lib/api/personas';
import type { PersonaConRoles } from '../../lib/api/types';
import { ETIQUETA_ROL } from '../../lib/api/types';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/layout/PageHeader';
import { ModalGestionRoles } from './ModalGestionRoles';

type TipoFiltro = 'todos' | 'comision';

interface StatItem {
  label: string;
  value: string;
  meta: string;
  valueColor?: 'default' | 'admin-red';
}

function Estadistica({ item }: { item: StatItem }) {
  const colorClass =
    item.valueColor === 'admin-red' ? 'text-admin-red' : 'text-text-primary';
  return (
    <div className="bg-bg-card border-[0.5px] border-white/[0.08] rounded-md p-4">
      <div className="text-xs uppercase tracking-wide text-text-secondary font-medium">
        {item.label}
      </div>
      <div className={`text-2xl font-medium mt-1 ${colorClass}`}>{item.value}</div>
      <div className="text-xs text-text-muted mt-1">{item.meta}</div>
    </div>
  );
}

function RolePill({ rol }: { rol: string }) {
  const label = ETIQUETA_ROL[rol] ?? rol;
  // Colores diferenciados por rol (paleta del handoff).
  const cls =
    rol === 'super_admin'
      ? 'bg-admin-red/20 text-admin-red'
      : rol === 'tesorero'
      ? 'bg-orange/20 text-orange'
      : rol === 'comision'
      ? 'bg-state-success/20 text-state-success'
      : 'bg-bg-sunken text-text-secondary';
  return (
    <span
      className={`inline-flex items-center px-2 py-[2px] rounded-xs text-xs font-medium mr-1 mb-1 ${cls}`}
    >
      {label}
    </span>
  );
}

function FilaPersona({
  persona,
  onEditar,
}: {
  persona: PersonaConRoles;
  onEditar: (p: PersonaConRoles) => void;
}) {
  const esAdmin = persona.tipo_cuenta === 'super_admin';
  const filaBg = esAdmin ? 'bg-admin-red/[0.05]' : '';
  const iniciales = persona.nombre_completo
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');
  const casa =
    persona.lote_identificador && persona.lote_sector
      ? `${persona.lote_sector} ${persona.lote_identificador}`
      : '—';

  return (
    <tr className={`border-b-[0.5px] border-white/[0.08] ${filaBg}`} data-testid="fila-persona">
      <td className="px-4 py-3">
        <div className="flex gap-2 items-center">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
              esAdmin ? 'bg-admin-red/20 text-admin-red' : 'bg-bg-sunken text-text-primary'
            }`}
          >
            {iniciales || '?'}
          </div>
          <div>
            <div className="text-sm font-medium text-text-primary">
              {persona.nombre_completo}
            </div>
            {persona.email && (
              <div className="text-xs text-text-muted">{persona.email}</div>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-text-secondary">{casa}</td>
      <td className="px-4 py-3">
        {persona.roles_vigentes.length === 0 ? (
          <span className="text-xs text-text-muted">Sin roles</span>
        ) : (
          <div className="flex flex-wrap">
            {persona.roles_vigentes.map((rol) => (
              <RolePill key={rol} rol={rol} />
            ))}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {esAdmin ? (
          <span className="text-xs text-text-muted">No editable</span>
        ) : (
          <Button variant="ghost" size="sm" onClick={() => onEditar(persona)}>
            Editar roles →
          </Button>
        )}
      </td>
    </tr>
  );
}

export function RolesPage() {
  const [personas, setPersonas] = useState<PersonaConRoles[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [tipo, setTipo] = useState<TipoFiltro>('todos');
  const [refreshKey, setRefreshKey] = useState(0);
  const [personaSeleccionada, setPersonaSeleccionada] =
    useState<PersonaConRoles | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    listPersonas({ search: searchDebounced || undefined })
      .then((data) => {
        if (!cancelado) setPersonas(data);
      })
      .catch((err) => {
        if (cancelado) return;
        const mensaje =
          err instanceof ApiError
            ? `Error ${err.status} al cargar personas`
            : 'Error de red';
        setError(mensaje);
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [searchDebounced, refreshKey]);

  // Filtro client-side por tipo.
  const personasFiltradas = useMemo(() => {
    if (tipo === 'comision')
      return personas.filter((p) => p.roles_vigentes.includes('comision'));
    return personas;
  }, [personas, tipo]);

  // Stats.
  const stats: StatItem[] = useMemo(() => {
    const total = personas.length;
    const admins = personas.filter((p) =>
      p.roles_vigentes.includes('super_admin'),
    ).length;
    const comision = personas.filter((p) =>
      p.roles_vigentes.includes('comision'),
    ).length;
    const tesorero = personas.filter((p) =>
      p.roles_vigentes.includes('tesorero'),
    ).length;
    // Sprint 12 S12-17: 4 roles. Eliminada la card de "Comisión-gestión"
    // por fusion doctrinaria. Mantenemos 4 cards.
    return [
      {
        label: 'Total cuentas',
        value: String(total),
        meta: `${total - admins} vecinos + ${admins} admin`,
      },
      {
        label: 'Comisión',
        value: String(comision),
        meta: 'Lectura amplia + aprobar solicitudes',
      },
      {
        label: 'Tesorero',
        value: String(tesorero),
        meta: 'Operaciones financieras',
      },
      {
        label: 'Sin roles',
        value: String(personas.filter((p) => p.roles_vigentes.length === 0).length),
        meta: 'No registrados',
      },
    ];
  }, [personas]);

  function handleEditar(persona: PersonaConRoles) {
    setPersonaSeleccionada(persona);
  }

  function handleModalCerrado() {
    setPersonaSeleccionada(null);
  }

  function handleRolesActualizados() {
    setPersonaSeleccionada(null);
    setRefreshKey((k) => k + 1);
  }

  return (
    <div className="px-8 py-6 space-y-6">
      <PageHeader
        eyebrow="Mantenedor — Cuentas operativas"
        title="Gestión de roles"
        description={`${personas.length} cuenta${personas.length === 1 ? '' : 's'} en el sistema.`}
      />

      <div
        className="bg-admin-red/[0.08] border-l-4 border-admin-red p-4 rounded-sm flex gap-3"
        role="alert"
      >
        <div className="flex-1">
          <div className="text-base text-admin-red font-medium mb-1">
            Cuenta de poder limitado
          </div>
          <div className="text-sm text-text-secondary">
            Esta cuenta solo gestiona asignación de roles del sistema.{' '}
            <strong className="text-text-primary">
              No ejecuta acciones operativas
            </strong>{' '}
            (no aprueba pagos, no edita datos de vecinos, no crea contenido). Si
            hay urgencia operativa, asignar el rol correspondiente a otra
            persona y que ella ejecute.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats.map((s) => (
          <Estadistica key={s.label} item={s} />
        ))}
      </div>

      <div className="flex gap-3 flex-wrap items-center">
        <input
          type="text"
          aria-label="Buscar por nombre, RUT o email"
          placeholder="Buscar por nombre, RUT, email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[240px] py-2 px-3 bg-bg-sunken border-[0.5px] border-white/15 rounded-sm text-text-primary placeholder:text-text-muted focus:border-admin-red focus:ring-1 focus:ring-admin-red/30 outline-none"
        />
        <ChipFiltro
          activo={tipo === 'todos'}
          onClick={() => setTipo('todos')}
          label="Todos"
        />
        <ChipFiltro
          activo={tipo === 'comision'}
          onClick={() => setTipo('comision')}
          label="Solo comisión"
        />
      </div>

      {loading && <p className="text-sm text-text-muted">Cargando personas…</p>}
      {error && !loading && (
        <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
          {error}
        </div>
      )}
      {!loading && !error && personasFiltradas.length === 0 && (
        <p className="text-sm text-text-muted">
          {personas.length === 0
            ? 'No hay personas cargadas.'
            : 'Ninguna persona coincide con el filtro seleccionado.'}
        </p>
      )}
      {!loading && !error && personasFiltradas.length > 0 && (
        <div className="bg-bg-card border-[0.5px] border-white/[0.08] rounded-md overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b-[0.5px] border-white/15 bg-bg-sunken">
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-text-secondary font-medium">
                  Persona
                </th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-text-secondary font-medium">
                  Casa
                </th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-text-secondary font-medium">
                  Roles
                </th>
                <th className="text-right px-4 py-2 text-xs uppercase tracking-wide text-text-secondary font-medium">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {personasFiltradas.map((p) => (
                <FilaPersona key={p.id} persona={p} onEditar={handleEditar} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {personaSeleccionada && (
        <ModalGestionRoles
          persona={personaSeleccionada}
          onCerrar={handleModalCerrado}
          onActualizado={handleRolesActualizados}
        />
      )}
    </div>
  );
}

interface ChipFiltroProps {
  activo: boolean;
  onClick: () => void;
  label: string;
}

function ChipFiltro({ activo, onClick, label }: ChipFiltroProps) {
  const className = activo
    ? 'px-3 py-1 bg-admin-red/15 text-admin-red rounded-full text-sm font-medium cursor-pointer'
    : 'px-3 py-1 bg-bg-sunken text-text-secondary rounded-full text-sm cursor-pointer hover:text-text-primary';
  return (
    <button type="button" className={className} onClick={onClick} aria-pressed={activo}>
      {label}
    </button>
  );
}
